import { CURRENCY_NOT_SET, formatUnitCost, isCurrencySet } from './currency';
import { NO_AMOUNT } from './decimal';

/**
 * Money figures for the metric cards on the account-level pages. Amounts are
 * only ever added within one currency (a row's own), exactly. A row with
 * no currency is counted apart, never folded into another currency and never
 * guessed.
 */
export interface CurrencyTotal { currency: string; amount: number }

export interface MoneyTotals {
  totals: CurrencyTotal[];
  /** Rows that have an amount but no currency. */
  unsetCount: number;
}

/** Decimal places kept when adding; wider than any currency's minor unit (BHD, KWD and others use 3). */
const SCALE_DIGITS = 6;
const SCALE = BigInt(1000000); // 10^SCALE_DIGITS

/**
 * An amount as an integer count of 10^-6 units, read from its exact decimal text
 * (the API sends strings like "100.10"), so adding never drifts and no currency
 * is cut to two places. Null when it is not a plain decimal number.
 */
function toScaledUnits(raw: number | string | null | undefined): bigint | null {
  if (raw === null || raw === undefined || raw === '') return null;
  let text = typeof raw === 'number' ? (Number.isFinite(raw) ? String(raw) : '') : raw.trim();
  if (/e/i.test(text) && typeof raw === 'number') text = raw.toFixed(SCALE_DIGITS);
  const m = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(text);
  if (!m || (m[2] === '' && (m[3] ?? '') === '')) return null;
  const frac = (m[3] ?? '').padEnd(SCALE_DIGITS, '0').slice(0, SCALE_DIGITS);
  const units = BigInt(m[2] || '0') * SCALE + BigInt(frac);
  return m[1] === '-' ? -units : units;
}

export function totalsByCurrency<T>(
  rows: readonly T[],
  amountOf: (row: T) => number | string | null | undefined,
  currencyOf: (row: T) => string | null | undefined,
): MoneyTotals {
  const sums = new Map<string, bigint>();
  let unsetCount = 0;
  for (const row of rows) {
    const units = toScaledUnits(amountOf(row));
    if (units === null) continue;
    const currency = currencyOf(row);
    if (!isCurrencySet(currency)) { unsetCount += 1; continue; }
    const code = currency.trim().toUpperCase();
    sums.set(code, (sums.get(code) ?? BigInt(0)) + units);
  }
  return {
    totals: [...sums.entries()].map(([currency, u]) => ({ currency, amount: Number(u) / Number(SCALE) })),
    unsetCount,
  };
}

/** "€300.30 · $50", one figure per currency; "currency not set" when that is all there is; a dash (NO_AMOUNT) for nothing. */
export function moneyTotalsText({ totals, unsetCount }: MoneyTotals): string {
  const parts = totals.map(t => formatUnitCost(t.amount, t.currency));
  if (parts.length === 0) return unsetCount > 0 ? CURRENCY_NOT_SET : NO_AMOUNT;
  if (unsetCount > 0) parts.push(`${unsetCount} ${CURRENCY_NOT_SET}`);
  return parts.join(' · ');
}
